import React, { ReactNode, useCallback, useContext } from 'react'
import L2Surface from '../l2/L2Surface'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTheme } from '@emotion/react'
import L6FormContext from '../l6/L6FormContext'
import { ConceptStatusValidity } from "@fusion-advantage/backcompat"
import { Concept, ConceptStatusValidityInfo, ValiditySeverity } from '@fusion-advantage/formlib'
import { validitySeverityIndicatorProps } from '../../uiv2/Indicator/StatusIndicatorProps'

const ValidationSurface = styled(L2Surface)(({theme}) => ({
	rowGap: theme.spacing(2),
}))

const Section = styled.div()

const SectionHeader = styled.div(({theme}) => ({
	...theme.baseStyles.inputLabel,
}))

interface ListProps {
	className?: string
	children?: ReactNode,
}

const List = styled(
	({className, children}: ListProps) => <ul className={`fa-ul ${className}`}>{children}</ul>
)(
	({theme}) => ({
		margin: `0 0 0 1.5em`,
		lineHeight: '1.8em',
		svg: {
			marginTop: '2px',
		},
	})
)

const ErrorLink = styled.a({
	textDecoration: 'none',
	color: 'inherit',
	'&:hover': {
		textDecoration: 'underline',
	},
})

interface L3ValidationSummarySeverityIconProps {
	severity: ValiditySeverity
}

const L3ValidationSummarySeverityIcon = ({severity}: L3ValidationSummarySeverityIconProps) => {
	const theme = useTheme()
	const { color, icon } = validitySeverityIndicatorProps(severity)
	return (
		<FontAwesomeIcon
			color={theme.colors[color]}
			icon={icon}
			listItem
		/>
	)
}

interface L3ValidationSummaryProps {
	sections: L3ValidationSummarySection[]
}

export interface L3ValidationSummarySection {
	title: string
	validities: L3ValidationSummaryValidity[]
}

export interface L3ValidationSummaryValidity {
	validity: ConceptStatusValidity
	concept: Concept
}

const L3ValidationSummary = ({sections = []}: L3ValidationSummaryProps) => {
	const { jumpToConcept } = useContext(L6FormContext)
	const handleErrClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, line: L3ValidationSummaryValidity) => {
		e.preventDefault()
		// console.debug(err.concept.xpaths)
		jumpToConcept(line.concept)
	}, [jumpToConcept])
	return (
		<ValidationSurface tier={0} outline={true}>
			{sections.map((section, idx) => (
				<Section key={idx}>
					<SectionHeader>{section.title}</SectionHeader>
					<List>
						{section.validities.length === 0 ? (
							<li>
								<L3ValidationSummarySeverityIcon severity={ValiditySeverity.Success} />
								Ready for submission
							</li>
						) : section.validities.map((line, idx) => {
							const validityInfo = new ConceptStatusValidityInfo(line.validity)
							return (
								<li key={idx}>
									<ErrorLink href="#" onClick={(e) => handleErrClick(e, line)}>
										<L3ValidationSummarySeverityIcon severity={validityInfo.severity} />
										{validityInfo.message(line.concept)}
									</ErrorLink>
								</li>
							)
						})}
					</List>
				</Section>
			))}
		</ValidationSurface>
	)
}

export default L3ValidationSummary
